import { Routes, Route, Navigate } from 'react-router-dom'
import { Suspense, useEffect } from 'react'
import { UserProvider, useUser } from './contexts/UserContext'
import { NotificationProvider } from './components/NotificationManager'
import PWAInstallPrompt from './components/PWAInstallPrompt'
import OfflineIndicator from './components/OfflineIndicator'
import ServiceWorkerUpdatePrompt from './components/ServiceWorkerUpdatePrompt'
import ProtectedRoute from './components/ProtectedRoute'
import AdminRouteGuard from './components/AdminRouteGuard'
import UserLayout from './layouts/UserLayout'
import ErrorBoundary from './components/ErrorBoundary'
import persistentStorage from './utils/persistentStorage'
import logger from './utils/logger'
import { lazyWithRetry } from './utils/lazyWithRetry'

// Lazy load pages for better performance (with chunk retry after deploys)
const LandingPage = lazyWithRetry(() => import('./pages/LandingPage'), 'LandingPage')
const RequestHandoff = lazyWithRetry(() => import('./pages/RequestHandoff'))
const TestPage = lazyWithRetry(() => import('./pages/TestPage'))
const Dashboard = lazyWithRetry(() => import('./pages/Dashboard'))
const FeedSessions = lazyWithRetry(() => import('./pages/FeedSessions'))
const Analytics = lazyWithRetry(() => import('./pages/Analytics'))
const Settings = lazyWithRetry(() => import('./pages/Settings'))
const Login = lazyWithRetry(() => import('./pages/Login'))
const Register = lazyWithRetry(() => import('./pages/Register'))
const ForgotPassword = lazyWithRetry(() => import('./pages/ForgotPassword'))
const ResetPassword = lazyWithRetry(() => import('./pages/ResetPassword'))

// Feed-specific pages
const Rations = lazyWithRetry(() => import('./pages/Rations'))
const Inventory = lazyWithRetry(() => import('./pages/Inventory'))
const FeedList = lazyWithRetry(() => import('./pages/FeedList'))
const FeedItemEdit = lazyWithRetry(() => import('./pages/FeedItemEdit'))
const FileCenter = lazyWithRetry(() => import('./pages/FileCenter'))
const Help = lazyWithRetry(() => import('./pages/Help'))
const Account = lazyWithRetry(() => import('./pages/Account'))
const Notifications = lazyWithRetry(() => import('./pages/Notifications'))
const NotificationSettings = lazyWithRetry(() => import('./pages/NotificationSettings'))
const RecordsAndCharts = lazyWithRetry(() => import('./pages/RecordsAndCharts'))
const FeedingCalendar = lazyWithRetry(() => import('./pages/FeedingCalendar'))

// Admin pages
const Admin = lazyWithRetry(() => import('./pages/Admin'))
const AdminUserManagementHierarchical = lazyWithRetry(() => import('./pages/AdminUserManagementHierarchical'))
const SubscriptionManagement = lazyWithRetry(() => import('./pages/SubscriptionManagement'))
const GlobalFeedLibrary = lazyWithRetry(() => import('./pages/GlobalFeedLibrary'))
const AdminCorporateAnalytics = lazyWithRetry(() => import('./pages/AdminCorporateAnalytics'))
const TutorialManagement = lazyWithRetry(() => import('./pages/admin/TutorialManagement'))
const TokenMonitoring = lazyWithRetry(() => import('./pages/admin/TokenMonitoring'))
const ParserPatterns = lazyWithRetry(() => import('./pages/admin/ParserPatterns'))
const SupportTickets = lazyWithRetry(() => import('./pages/SupportTickets'))

// Account Management pages
const CorporateDashboard = lazyWithRetry(() => import('./pages/CorporateDashboard'))
const CorporateBilling = lazyWithRetry(() => import('./pages/CorporateBilling'))
const CorporateUserManagement = lazyWithRetry(() => import('./pages/CorporateUserManagement'))
const CorporateMemberManagement = lazyWithRetry(() => import('./pages/CorporateMemberManagement'))
const Team = lazyWithRetry(() => import('./pages/Team'))
const FarmManagement = lazyWithRetry(() => import('./pages/FarmManagement'))
const CorporateFeedLibrary = lazyWithRetry(() => import('./pages/CorporateFeedLibrary'))
import CorporateManagerRoute from './components/CorporateManagerRoute'
const MultiFarmDashboard = lazyWithRetry(() => import('./pages/MultiFarmDashboard'))
const CurrencyRates = lazyWithRetry(() => import('./pages/CurrencyRates'))

// Public pages
const PublicRation = lazyWithRetry(() => import('./pages/PublicRation'))
const AcceptInvitation = lazyWithRetry(() => import('./pages/AcceptInvitation'))
const SetPassword = lazyWithRetry(() => import('./pages/SetPassword'))
const MaintenancePage = lazyWithRetry(() => import('./pages/MaintenancePage'))

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
  </div>
)

// Role-based dashboard component
function RoleDashboard() {
  const { 
    user, 
    selectedAdminAccount, 
    selectedCorporateFarm, 
    selectedFarm,
    isCorporateManager, 
    isCorporateMember,
    userFarms
  } = useUser();
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Admin users go to admin panel ONLY if they haven't selected a farm account
  // If they have a selected account, show them the farm dashboard
  if (user.role === 'ADMIN' && !selectedAdminAccount) {
    return <Navigate to="/admin" replace />;
  }
  
  // Corporate users: MUST be checked BEFORE SUB_USER to prevent corporate members from being misrouted
  // Note: INDIVIDUAL users with corporateAccountId are farm owners under corporate billing, NOT corporate users
  const isCorporate = user.role === 'CORPORATE' || isCorporateManager || isCorporateMember;
  
  if (isCorporate) {
    // If they have selected a farm, show farm dashboard
    if (selectedCorporateFarm?.farm?.id) {
      return <Dashboard />;
    }
    // Otherwise show corporate dashboard (for BOTH managers and members)
    return <CorporateDashboard />;
  }
  
  // Sub-users: handle single vs multi-farm logic (but NOT corporate employees)
  if (user.role === 'SUB_USER' && !isCorporateMember) {
    // Single farm user: always show regular dashboard (they can't choose)
    if (userFarms.length === 1) {
      return <Dashboard />;
    }
    // Multi-farm user: redirect to multi-farm dashboard if no farm selected
    // They should use /multi-farm-dashboard route to see overview
    if (!selectedFarm?.farmId) {
      return <Navigate to="/multi-farm-dashboard" replace />;
    }
    // Multi-farm user with selection: show farm dashboard
    return <Dashboard />;
  }
  
  // Individual farm owners, sub-users with single farm, and admins with selected accounts use the modern farm dashboard
  return <Dashboard />;
}

function App() {
  // Check if we're on the main domain and should show maintenance
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isMainDomain = hostname === 'feed.cowdemy.com' || hostname === 'www.feed.cowdemy.com';
  
  // Check for maintenance mode via environment variable (for Vercel toggle)
  const maintenanceMode = import.meta.env.VITE_MAINTENANCE_MODE === 'true';
  
  // Show maintenance page ONLY on main domain AND when maintenance mode is enabled
  if (isMainDomain && maintenanceMode) {
    return (
      <Suspense fallback={<PageLoader />}>
        <MaintenancePage />
      </Suspense>
    );
  }

  // ✅ PWA LIFECYCLE: Enhanced auth persistence for mobile PWAs
  useEffect(() => {
    const handleBeforeUnload = async () => {
      // Ensure data is persisted before app closes
      try {
        const storageInfo = await persistentStorage.getStorageInfo();
        logger.debug('PWA closing - storage state:', storageInfo);
      } catch (error) {
        logger.error('Error checking storage before unload:', error);
      }
    };

    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'hidden') {
        // App is being backgrounded/closed
        logger.debug('PWA app hidden - preserving auth state');
      } else if (document.visibilityState === 'visible') {
        // App is being brought back to foreground
        logger.debug('PWA app visible - checking auth state');
      }
    };

    const handleAppStateChange = (event: Event) => {
      // Handle PWA app state changes
      logger.debug('PWA app state change:', event.type);
    };

    // Add PWA lifecycle event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleAppStateChange);
    window.addEventListener('blur', handleAppStateChange);

    // Clean up listeners
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleAppStateChange);
      window.removeEventListener('blur', handleAppStateChange);
    };
  }, []);

  return (
    <UserProvider>
      <NotificationProvider>
        <ErrorBoundary componentName="App">
          <Suspense fallback={<PageLoader />}>
            <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/request" element={<RequestHandoff />} />
          <Route path="/test" element={<TestPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/ration/:token" element={<PublicRation />} />
          <Route path="/accept-invitation/:token" element={<AcceptInvitation />} />
          <Route path="/set-password/:token" element={<SetPassword />} />

        {/* Protected Routes with UserLayout */}
        <Route element={<ProtectedRoute><UserLayout /></ProtectedRoute>}>
          {/* Dashboard Routes - Role-based */}
          <Route path="/dashboard" element={<RoleDashboard />} />
          <Route path="/farm-dashboard" element={<Dashboard />} />
          <Route path="/worker-dashboard" element={<Dashboard />} />
          <Route path="/multi-farm-dashboard" element={<MultiFarmDashboard />} />

          {/* Feed Management Routes */}
          <Route path="/rations/:id" element={<Rations />} />
          <Route path="/rations" element={<Rations />} />
          <Route path="/inventory" element={<Inventory />} />
          <Route path="/feed-list/:id" element={<FeedItemEdit />} />
          <Route path="/feed-list" element={<FeedList />} />
          <Route path="/file-center" element={<FileCenter />} />
          <Route path="/records-and-charts" element={<RecordsAndCharts />} />
          <Route path="/feeding-calendar" element={<FeedingCalendar />} />
          <Route path="/help" element={<Help />} />

          {/* User Account Routes */}
          <Route path="/account" element={<Account />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/notification-settings" element={<NotificationSettings />} />
          <Route path="/currency-rates" element={<CurrencyRates />} />
          
          {/* Account Management Routes */}
          <Route path="/corporate-dashboard" element={<CorporateDashboard />} />
          <Route path="/corporate/billing" element={<CorporateManagerRoute><CorporateBilling /></CorporateManagerRoute>} />
          <Route path="/corporate/users" element={<CorporateManagerRoute><CorporateUserManagement /></CorporateManagerRoute>} />
          <Route path="/corporate/team" element={<CorporateMemberManagement />} />
          <Route path="/corporate/feed-library" element={<CorporateFeedLibrary />} />
          <Route path="/team" element={<Team />} />
          <Route path="/support-tickets" element={<SupportTickets />} />
          <Route path="/farm-management" element={<FarmManagement />} />

          {/* Legacy Routes (for backward compatibility) */}
          <Route path="/sessions" element={<FeedSessions />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/settings" element={<Settings />} />

          {/* Admin Routes - Protected by AdminRouteGuard */}
          <Route path="/admin" element={<AdminRouteGuard><Admin /></AdminRouteGuard>} />
          <Route path="/admin/users" element={<AdminRouteGuard><AdminUserManagementHierarchical /></AdminRouteGuard>} />
          <Route path="/admin/user-management" element={<AdminRouteGuard><AdminUserManagementHierarchical /></AdminRouteGuard>} />
          <Route path="/admin/subscriptions" element={<AdminRouteGuard><SubscriptionManagement /></AdminRouteGuard>} />
          <Route path="/admin/landing-page" element={<Navigate to="/admin" replace />} />
          <Route path="/admin/global-feed-library" element={<AdminRouteGuard><GlobalFeedLibrary /></AdminRouteGuard>} />
          <Route path="/admin/corporate/:corporateId" element={<AdminRouteGuard><AdminCorporateAnalytics /></AdminRouteGuard>} />
          <Route path="/admin/tutorials" element={<AdminRouteGuard><TutorialManagement /></AdminRouteGuard>} />
          <Route path="/admin/token-usage" element={<AdminRouteGuard><TokenMonitoring /></AdminRouteGuard>} />
          <Route path="/admin/parser-patterns" element={<AdminRouteGuard><ParserPatterns /></AdminRouteGuard>} />
        </Route>
        </Routes>
          </Suspense>
        </ErrorBoundary>
        {/* PWA Install Prompt - shown when appropriate */}
        <PWAInstallPrompt />
        {/* Service Worker Update Prompt */}
        <ServiceWorkerUpdatePrompt />
        {/* Offline Indicator */}
        <OfflineIndicator />
      </NotificationProvider>
    </UserProvider>
  )
}

export default App
